import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as AttrUtils from 'Util/AttributeUtils';
import * as Enums from '../Enums';

export interface IDiscountEntityAttributes extends IModelAttributes {
	name: string;
	code: string;
	bookingWindowStartDate: Date | null;
	bookingWindowEndDate: Date | null;
	tripWindowStartDate: Date | null;
	tripWindowEndDate: Date | null;
	active: boolean;
	discountType: Enums.discountType;
	discountScaling: number;
	legacyId: number;

	alterations: Array<Models.DiscountAlteration | Models.IDiscountAlterationAttributes>;
	transactions: Array<Models.TransactionDiscount | Models.ITransactionDiscountAttributes>;
	description: string;
	calculationType: Enums.discountCalculationType;
	isRestricted: boolean;
	bookingCreation: boolean;
	bookingAlteration: boolean;
	forEventBookings: boolean;
	forPassengerOnlyBookings: boolean;
	forVehicleBookings: boolean;
	forFreightBookings: boolean;
	canBeAppliedWithOtherDiscounts: boolean;
	useCount: number;
	returnBookingsOnly: boolean;
	oneWayBookingsOnly: boolean;
	requireSameDayReturn: boolean;

	passengerCountRules: Array<Models.DiscountPassengerCountRule | Models.IDiscountPassengerCountRuleAttributes>;
}
@entity('DiscountEntity', 'Discount')
export default class DiscountEntity extends Model implements IDiscountEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<DiscountEntity, string>()
	public name: string;

	@observable
	@attribute<DiscountEntity, string>()
	public description: string;

	@observable
	@attribute<DiscountEntity, string>()
	public code: string;

	@observable
	@attribute<DiscountEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public bookingWindowStartDate: Date | null;

	@observable
	@attribute<DiscountEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public bookingWindowEndDate: Date | null;

	@observable
	@attribute<DiscountEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public tripWindowStartDate: Date | null;

	@observable
	@attribute<DiscountEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public tripWindowEndDate: Date | null;

	@observable
	@attribute<DiscountEntity, boolean>()
	public active: boolean;

	@observable
	@attribute<DiscountEntity, Enums.discountType>()
	public discountType: Enums.discountType;

	@observable
	@attribute<DiscountEntity, number>()
	public discountScaling: number;

	@observable
	@attribute<DiscountEntity, number>()
	public legacyId: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public alterations: Models.DiscountAlteration[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public transactions: Models.TransactionDiscount[] = [];

	@observable
	@attribute<DiscountEntity, Enums.discountCalculationType>()
	public calculationType: Enums.discountCalculationType;

	@observable
	@attribute<DiscountEntity, boolean>()
	public isRestricted: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public bookingCreation: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public bookingAlteration: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public forEventBookings: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public forPassengerOnlyBookings: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public forVehicleBookings: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public forFreightBookings: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public canBeAppliedWithOtherDiscounts: boolean;

	public useCount: number;

	@observable
	@attribute<DiscountEntity, boolean>()
	public returnBookingsOnly: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public oneWayBookingsOnly: boolean;

	@observable
	@attribute<DiscountEntity, boolean>()
	public requireSameDayReturn: boolean;

	/**
	 * Eligibility rules: each rule pairs a passenger-count range with a disjoint
	 * set of passenger types. All rules must be satisfied per leg for the
	 * discount to apply. Hidden from the auto-CRUD form — managed via a
	 * dedicated editor.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerCountRules: Models.DiscountPassengerCountRule[] = [];

	constructor(attributes?: Partial<IDiscountEntityAttributes>) {
		super(attributes);

		this.alterations = [];
		this.transactions = [];
		this.passengerCountRules = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IDiscountEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.code !== undefined) {
				this.code = attributes.code;
			}
			if (attributes.bookingWindowStartDate !== undefined) {
				if (attributes.bookingWindowStartDate === null) {
					this.bookingWindowStartDate = attributes.bookingWindowStartDate;
				} else {
					this.bookingWindowStartDate = moment(attributes.bookingWindowStartDate).toDate();
				}
			}
			if (attributes.bookingWindowEndDate !== undefined) {
				if (attributes.bookingWindowEndDate === null) {
					this.bookingWindowEndDate = attributes.bookingWindowEndDate;
				} else {
					this.bookingWindowEndDate = moment(attributes.bookingWindowEndDate).toDate();
				}
			}
			if (attributes.tripWindowStartDate !== undefined) {
				if (attributes.tripWindowStartDate === null) {
					this.tripWindowStartDate = attributes.tripWindowStartDate;
				} else {
					this.tripWindowStartDate = moment(attributes.tripWindowStartDate).toDate();
				}
			}
			if (attributes.tripWindowEndDate !== undefined) {
				if (attributes.tripWindowEndDate === null) {
					this.tripWindowEndDate = attributes.tripWindowEndDate;
				} else {
					this.tripWindowEndDate = moment(attributes.tripWindowEndDate).toDate();
				}
			}
			if (attributes.active !== undefined) {
				this.active = attributes.active;
			}
			if (attributes.discountType !== undefined) {
				this.discountType = attributes.discountType;
			}
			if (attributes.discountScaling !== undefined) {
				this.discountScaling = attributes.discountScaling;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.alterations !== undefined && Array.isArray(attributes.alterations)) {
				if (!this.alterations) {
					this.alterations = [];
				}
				for (const model of attributes.alterations) {
					if (model instanceof Models.DiscountAlteration) {
						this.alterations.push(model);
					} else {
						this.alterations.push(new Models.DiscountAlteration(model));
					}
				}
			}
			if (attributes.transactions !== undefined && Array.isArray(attributes.transactions)) {
				if (!this.transactions) {
					this.transactions = [];
				}
				for (const model of attributes.transactions) {
					if (model instanceof Models.TransactionDiscount) {
						this.transactions.push(model);
					} else {
						this.transactions.push(new Models.TransactionDiscount(model));
					}
				}
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.calculationType !== undefined) {
				this.calculationType = attributes.calculationType;
			}
			if (attributes.isRestricted !== undefined) {
				this.isRestricted = attributes.isRestricted;
			}
			if (attributes.bookingCreation !== undefined) {
				this.bookingCreation = attributes.bookingCreation;
			}
			if (attributes.bookingAlteration !== undefined) {
				this.bookingAlteration = attributes.bookingAlteration;
			}
			if (attributes.forEventBookings !== undefined) {
				this.forEventBookings = attributes.forEventBookings;
			}
			if (attributes.forPassengerOnlyBookings !== undefined) {
				this.forPassengerOnlyBookings = attributes.forPassengerOnlyBookings;
			}
			if (attributes.forVehicleBookings !== undefined) {
				this.forVehicleBookings = attributes.forVehicleBookings;
			}
			if (attributes.forFreightBookings !== undefined) {
				this.forFreightBookings = attributes.forFreightBookings;
			}
			if (attributes.canBeAppliedWithOtherDiscounts !== undefined) {
				this.canBeAppliedWithOtherDiscounts = attributes.canBeAppliedWithOtherDiscounts;
			}
			if (attributes.useCount !== undefined) {
				this.useCount = attributes.useCount;
			}
			if (isNotNullOrUndefined(attributes.oneWayBookingsOnly)) {
				this.oneWayBookingsOnly = attributes.oneWayBookingsOnly;
			}
			if (isNotNullOrUndefined(attributes.returnBookingsOnly)) {
				this.returnBookingsOnly = attributes.returnBookingsOnly;
			}
			if (isNotNullOrUndefined(attributes.requireSameDayReturn)) {
				this.requireSameDayReturn = attributes.requireSameDayReturn;
			}
			if (attributes.passengerCountRules !== undefined && Array.isArray(attributes.passengerCountRules)) {
				if (!this.passengerCountRules) {
					this.passengerCountRules = [];
				}
				for (const model of attributes.passengerCountRules) {
					if (model instanceof Models.DiscountPassengerCountRule) {
						this.passengerCountRules.push(model);
					} else {
						this.passengerCountRules.push(new Models.DiscountPassengerCountRule(model));
					}
				}
			}
		}
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return `${this.name} (${this.code})`;
	}
}
